import { useState, type FormEvent } from 'react';
import { ApiRequestError, postJson, useAuth } from '../../api/auth.ts';
import { Banner, Button } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import logo from '../../theme/assets/logo.svg?raw';
import { inputClass, TotpSetupForm } from './TotpSetup.tsx';
import { RecoveryLogin } from './RecoveryLogin.tsx';
import { useConnection } from '../../api/connection.ts';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [setupToken, setSetupToken] = useState<string>();
  const [forgot, setForgot] = useState(false);
  const [recovery, setRecovery] = useState(false);
  // the emergency login exists only on the Pi itself (ADR-0016)
  const onDevice = useConnection((s) => s.mode === 'gateway');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await login({ email, password, totp: needsTotp ? totp : undefined });
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === 'totp_required') {
        setNeedsTotp(true);
      } else if (e instanceof ApiRequestError && e.code === 'totp_setup_required' && e.setupToken) {
        setSetupToken(e.setupToken);
        setPassword('');
      } else {
        setError(e instanceof ApiRequestError ? e.message : t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-10 text-center">
          <div
            className="mx-auto mb-3 text-text"
            style={{ width: 200 }}
            dangerouslySetInnerHTML={{ __html: logo }}
          />
          <p className="text-muted">{t('app.claim')}</p>
        </div>
        {recovery ? (
          <RecoveryLogin onBack={() => setRecovery(false)} />
        ) : setupToken ? (
          <TotpSetupForm setupToken={setupToken} onCancel={() => setSetupToken(undefined)} />
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <h1 className="text-xl font-semibold">{t('auth.title')}</h1>
            {error && <Banner tone="danger" title={error} />}
            <label className="block">
              <span className="text-sm text-muted">{t('auth.email')}</span>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className="text-sm text-muted">{t('auth.password')}</span>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
            </label>
            {needsTotp && (
              <label className="block">
                <span className="text-sm text-muted">{t('auth.totp')}</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  autoFocus
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
                  className={`${inputClass} tracking-[0.4em]`}
                />
                <span className="mt-1 block text-sm text-muted">{t('auth.totpHint')}</span>
              </label>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={busy || !email || !password || (needsTotp && totp.length !== 6)}
            >
              {t('auth.submit')}
            </Button>
            <button
              type="button"
              className="min-h-11 w-full text-sm text-muted underline hover:text-text"
              onClick={() => setForgot(!forgot)}
            >
              {t('reset.forgot')}
            </button>
            {/* the Pi has no e-mail: there STE or the installer sends a reset link (ADR-0015) */}
            {forgot &&
              (onDevice ? (
                <p className="text-sm text-muted">{t('reset.forgotText')}</p>
              ) : (
                <ForgotPassword initialEmail={email} />
              ))}
            {onDevice && (
              <button
                type="button"
                className="min-h-11 w-full text-sm text-muted underline hover:text-text"
                onClick={() => setRecovery(true)}
              >
                {t('recovery.open')}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/** "Passwort vergessen?" on STE Connect (ADR-0027): the link comes by e-mail */
function ForgotPassword({ initialEmail }: { initialEmail: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [error, setError] = useState<string>();

  async function send() {
    setState('busy');
    setError(undefined);
    try {
      await postJson('/password-reset/request', { email: email.trim() });
      setState('sent');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : t('common.error'));
      setState('idle');
    }
  }

  if (state === 'sent') return <Banner tone="accent" title={t('reset.forgotSent')} />;
  return (
    <div className="space-y-2 rounded-xl border border-border bg-surface-2 p-3">
      <p className="text-sm text-muted">{t('reset.forgotMail')}</p>
      <input
        type="email"
        autoComplete="username"
        aria-label={t('auth.email')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />
      {error && <Banner tone="danger" title={error} />}
      <Button
        variant="secondary"
        className="w-full"
        disabled={state === 'busy' || !email.includes('@')}
        onClick={() => void send()}
      >
        {t('reset.forgotSend')}
      </Button>
    </div>
  );
}
